import React, { useMemo } from "react";
import { WORLD_RINGS } from "./geo/worldRings";
import { COUNTRY_CENTROIDS } from "./geo/countryCentroids";
import { project, bubbleRadius, flagEmoji, countryName, MAP_VIEW } from "./geo/mapMath";
import type { CountryTraffic } from "../../hooks/useAnalytics";

// The land outline is static — project every ring once at module load into a single SVG path.
const LAND_PATH = WORLD_RINGS.map((ring) => {
	let d = "";
	for (let i = 0; i < ring.length; i += 1) {
		const { x, y } = project(ring[i][0], ring[i][1]);
		d += `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
	}
	return `${d}Z`;
}).join("");

/**
 * Dependency-free world map of visitor origins: a light land silhouette (equirectangular, Antarctica
 * cropped) with one proportional bubble per country, plotted at the country centroid and sized so the
 * bubble AREA tracks page-view volume. Hovering a bubble shows the country + count (native SVG title).
 * Countries Cloudflare reports without a known centroid (e.g. the Tor pseudo-country "T1") are skipped.
 */
export const WorldTrafficMap: React.FC<{ countries: CountryTraffic[]; locale?: string; className?: string }> = ({
	countries,
	locale = "fr",
	className,
}) => {
	const bubbles = useMemo(() => {
		const located = (countries ?? [])
			.map((c) => ({ country: c, centroid: COUNTRY_CENTROIDS[c.code] }))
			.filter((c): c is { country: CountryTraffic; centroid: readonly [number, number] } => Boolean(c.centroid));
		const max = located.reduce((m, c) => Math.max(m, c.country.count), 0);
		// Largest first so smaller bubbles paint on top and stay visible.
		return located
			.sort((a, b) => b.country.count - a.country.count)
			.map(({ country, centroid }) => {
				const { x, y } = project(centroid[0], centroid[1]);
				return { code: country.code, count: country.count, x, y, r: bubbleRadius(country.count, max, 3.5, 22) };
			});
	}, [countries]);

	return (
		<svg
			viewBox={`0 0 ${MAP_VIEW.width} ${MAP_VIEW.height}`}
			className={className}
			role="img"
			aria-label="Carte des visiteurs par pays"
			preserveAspectRatio="xMidYMid meet"
		>
			<path d={LAND_PATH} fill="#EAEFF5" stroke="#D9E2EC" strokeWidth={0.4} vectorEffect="non-scaling-stroke" />
			{bubbles.map((b) => (
				<circle
					key={b.code}
					cx={b.x}
					cy={b.y}
					r={b.r}
					fill="#0095DB"
					fillOpacity={0.5}
					stroke="#0078B0"
					strokeWidth={0.5}
					vectorEffect="non-scaling-stroke"
				>
					<title>{`${flagEmoji(b.code)} ${countryName(b.code, locale)} — ${b.count.toLocaleString("fr-FR")} pages vues`}</title>
				</circle>
			))}
		</svg>
	);
};

export default WorldTrafficMap;
